import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const TEXT_NODE = 3;

function AutoTranslate() {
  const { i18n } = useTranslation();
  const debounceRef = useRef(null);

  useEffect(() => {
    const t = i18n.getFixedT(i18n.language);
    const originalTextMap = new WeakMap();
    const originalAttributesMap = new WeakMap();

    const translateText = (text) => {
      const trimmed = text.replace(/\s+/g, ' ').trim();
      if (!trimmed) {
        return null;
      }
      const translated = t(trimmed, { defaultValue: trimmed });
      return translated !== trimmed ? translated : null;
    };

    const transformNode = (node) => {
      if (node.nodeType === TEXT_NODE) {
        const original = originalTextMap.get(node) || node.nodeValue;
        if (!originalTextMap.has(node)) {
          originalTextMap.set(node, original);
        }
        const translated = translateText(original);
        if (translated) {
          node.nodeValue = node.nodeValue.replace(node.nodeValue.trim(), translated);
        }
      }
    };

    const translateAttributes = (element) => {
      const originals = originalAttributesMap.get(element) || {};
      ['placeholder', 'title', 'aria-label'].forEach((attr) => {
        const currentValue = element.getAttribute(attr);
        if (!currentValue) return;

        const original = originals[attr] || currentValue;
        if (!originals[attr]) {
          originals[attr] = original;
          originalAttributesMap.set(element, originals);
        }

        const translated = translateText(original);
        if (translated) {
          element.setAttribute(attr, translated);
        }
      });
    };

    const walk = (node) => {
      transformNode(node);
      if (node.nodeType === 1) {
        translateAttributes(node);
      }
      node = node.firstChild;
      while (node) {
        walk(node);
        node = node.nextSibling;
      }
    };

    const translateAll = () => {
      if (document.body) {
        walk(document.body);
      }
    };

    const debouncedTranslate = () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        translateAll();
      }, 100); // Debounce for 100ms
    };

    const observer = new MutationObserver((mutations) => {
      let hasChanges = false;
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          hasChanges = true;
        }
        if (mutation.type === 'characterData') {
          hasChanges = true;
        }
        if (mutation.type === 'attributes') {
          hasChanges = true;
        }
      });
      if (hasChanges) {
        debouncedTranslate();
      }
    });

    translateAll();
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['placeholder', 'title', 'aria-label'],
    });

    return () => {
      observer.disconnect();
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [i18n, i18n.language]);

  return null;
}

export default AutoTranslate;
